import React, { useState, useEffect } from 'react';
import { FetchCampaignAds } from '../../../../Api/AdsApi';
import { Card, CardBody, CardHeader, CardImage, CardTitle } from '@progress/kendo-react-layout';
import { SvgIcon } from "@progress/kendo-react-common";
import { Loader } from '@progress/kendo-react-indicators';
import Viewer from 'react-viewer';
import { Dialog } from '@progress/kendo-react-dialogs'; // Import Dialog for Phone Modal
import { fullscreenIcon, trashIcon } from '@progress/kendo-svg-icons';
import "./AdsCard.css";
import { isoToMonthDateYear } from '../../../../helpers/helpers';
import phoneFrame from "../../../../Assets/Phone-Frame.png";
import { FaMobileAlt } from 'react-icons/fa'; // Import the phone icon

const AdsCard = ({ campaign }) => {
    const [adsList, setAdsList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [expandPicVisible, setExpandPicVisible] = useState([]);
    const [phoneModalVisible, setPhoneModalVisible] = useState(false); // Single state for phone modal visibility
    const [selectedAdIndex, setSelectedAdIndex] = useState(null); // Track which ad is selected

    const FetchAdsData = async () => {
        try {
            const adsListArray = await FetchCampaignAds({ SearchList: [{ "campaignId": campaign.id }] });
            setAdsList(adsListArray);
        } catch (error) {
            console.error("Error fetching ads data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setIsLoading(true);
        FetchAdsData();
    }, [campaign]);

    const toggleExpandPicDialog = (index) => {
        setExpandPicVisible((prev) => {
            const newState = [...prev];
            newState[index] = !newState[index];
            return newState;
        });
    };

    const togglePhoneModal = (index) => {
        setSelectedAdIndex(index); // Set the selected ad index
        setPhoneModalVisible((prev) => !prev); // Toggle the phone modal visibility
    };

    const closePhoneModal = () => {
        setPhoneModalVisible(false); // Close the phone modal
        setSelectedAdIndex(null); // Reset the selected ad index
    };

    if (isLoading) return <Loader style={{ marginLeft: "50%" }} className='infinite-spinner' size='large' type='infinite-spinner' />;

    return (
        <div className="ads-card-wrapper">
            {adsList.length > 0 ? (
                adsList.map((ad, index) => (
                    <div key={ad.id}>
                        <Card
                            style={{
                                minWidth: "300px",
                                width: '17vw',
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                                margin: "12px",
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <div style={{ position: 'relative', overflow: 'hidden' }}>
                                <CardImage
                                    src={ad.imageURL}
                                    style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                                    alt={`Ad Image - ${index}`}
                                />
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 14,
                                        left: 0,
                                        right: 14,
                                        display: 'flex',
                                        justifyContent: 'end',
                                        alignItems: 'center',
                                        transition: 'opacity 0.2s ease-in-out',
                                    }}
                                    className="icon-overlay"
                                >
                                    <SvgIcon
                                        icon={fullscreenIcon}
                                        size="xxlarge"
                                        themeColor="primary"
                                        onClick={() => toggleExpandPicDialog(index)}
                                        style={{ cursor: 'pointer', marginRight: '14px', backgroundColor: 'rgba(248, 248, 248, 0.9)', padding: "6px", borderRadius: '4px' }}
                                    />
                                    <SvgIcon
                                        icon={trashIcon}
                                        size="xxlarge"
                                        themeColor="secondary"
                                        style={{ cursor: 'pointer', backgroundColor: 'rgba(248, 248, 248, 0.9)', padding: "6px", borderRadius: '4px' }}
                                    />
                                </div>
                            </div>
                            <CardBody>
                                <p>{ad.body}</p>
                                <span style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center', // Add this to vertically align items
                                }}>
                                    <p style={{ margin: 0 }}><strong>Date Created:</strong> {isoToMonthDateYear(ad.dateCreated)}</p>
                                    <FaMobileAlt
                                        onClick={() => togglePhoneModal(index)}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: 'rgba(248, 248, 248, 0.9)',
                                            padding: "8px",
                                            borderRadius: '4px',
                                            fontSize: '34px', // Adjust icon size
                                            color: '#224db3', // Adjust icon color
                                        }}
                                    />
                                </span>
                            </CardBody>

                            {/* Image Viewer Modal (Kept as <Viewer>) */}
                            <Viewer
                                visible={expandPicVisible[index]}
                                onMaskClick={() => toggleExpandPicDialog(index)}
                                onClose={() => toggleExpandPicDialog(index)}
                                images={[{ src: ad.imageURL, alt: `Ad Image - ${index}` }]}
                                noNavbar
                                noToolbar
                                defaultScale={2.2}
                            />
                        </Card>
                    </div>
                ))
            ) : (
                <div className="no-campaigns" style={{ fontWeight: '500' }}>No Advertisements for this Campaign</div>
            )}

            {/* Phone Modal (Replaced with <Dialog>) */}
            {phoneModalVisible && (
                <Dialog
                    title={"Preview"}
                    visible={phoneModalVisible}
                    onClose={closePhoneModal}
                >
                    <div style={{ position: 'relative', width: '100%', height: '80vh' }}>
                        {/* Phone Frame */}
                        <img
                            src={phoneFrame}
                            alt="Phone Frame"
                            style={{ width: '100%', height: '100%' }}
                        />
                        {/* Ad Image Overlay */}
                        {selectedAdIndex !== null && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '47%', // Adjust as needed
                                    left: '6%',
                                    width: '79.9%', // Full width of the phone frame
                                    backgroundColor: 'white', // White background
                                    padding: '16px', // Add padding for spacing
                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', // Optional: Add shadow
                                }}
                            >
                                {/* Ad Body Text */}
                                <div
                                    style={{
                                        color: '#000', // Adjust text color as needed
                                        fontSize: '16px', // Adjust font size as needed
                                        marginBottom: '12px', // Add margin for spacing
                                        overflow: 'hidden', // Hide overflow
                                        display: '-webkit-box', // Use flexbox for line clamping
                                        WebkitLineClamp: 3, // Limit to 3 lines
                                        WebkitBoxOrient: 'vertical', // Vertical orientation for line clamping
                                        textOverflow: 'ellipsis', // Add ellipsis for truncated text
                                    }}
                                >
                                    {adsList[selectedAdIndex].body}
                                </div>
                                <img
                                    src={adsList[selectedAdIndex].imageURL}
                                    alt="Ad Image"
                                    style={{
                                        width: '100%', // Full width of the container
                                        // height: 'auto',
                                        height: "170px",
                                        borderRadius: '6px', // Optional: Add rounded corners
                                        objectFit: 'cover', // Ensures the image covers the container without distortion
                                    }}
                                />

                            </div>
                        )}
                    </div>
                </Dialog>
            )}
        </div>
    );
};

export default AdsCard;